import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, addDoc, Timestamp } from 'firebase/firestore';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../../firebase';
import { MaxLift } from '../../types';
import './MaxLiftsView.css';

enum LiftType {
  BenchPress = "Bench Press",
  Deadlift = "Deadlift",
  Squat = "Squat",
  Clean = "Clean"
}

const MaxLiftsView: React.FC = () => {
  const [user] = useAuthState(auth);
  const [maxLifts, setMaxLifts] = useState<MaxLift[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showingAddSheet, setShowingAddSheet] = useState(false);
  const [selectedLift, setSelectedLift] = useState<LiftType | null>(null);
  const [newMaxValue, setNewMaxValue] = useState<number>(0);
  const [showingConfirmation, setShowingConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (user) {
      loadMaxLifts();
    }
  }, [user]);

  const loadMaxLifts = async () => {
    if (!user?.uid) return;

    try {
      const maxLiftsRef = collection(db, 'maxLifts');
      const q = query(
        maxLiftsRef,
        where('userId', '==', user.uid),
        orderBy('dateCreated', 'desc')
      );
      
      const snapshot = await getDocs(q);
      const lifts: MaxLift[] = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          userId: data.userId,
          benchPress: data.benchPress,
          deadlift: data.deadlift,
          squat: data.squat,
          clean: data.clean,
          dateCreated: data.dateCreated.toDate()
        };
      });

      setMaxLifts(lifts);
      setIsLoading(false);
    } catch (error: any) {
      console.error('Error loading max lifts:', error);
      setErrorMessage(`Failed to load max lifts: ${error.message}`);
      setIsLoading(false);
    }
  };

  const lastMaxLift = maxLifts.length > 0 ? maxLifts[0] : null;
  const sortedMaxLifts = [...maxLifts].sort((a, b) => a.dateCreated.getTime() - b.dateCreated.getTime());

  const valueForLift = (lift: MaxLift, type: LiftType): number => {
    switch (type) {
      case LiftType.BenchPress: return lift.benchPress;
      case LiftType.Deadlift: return lift.deadlift;
      case LiftType.Squat: return lift.squat;
      case LiftType.Clean: return lift.clean;
    }
  };

  const getColorForLift = (type: LiftType): string => {
    switch (type) {
      case LiftType.BenchPress: return '#3b82f6';
      case LiftType.Deadlift: return '#ef4444';
      case LiftType.Squat: return '#10b981';
      case LiftType.Clean: return '#f59e0b';
    }
  };

  const saveMaxLift = async () => {
    if (!user?.uid || !selectedLift || newMaxValue <= 0) return;

    setIsSaving(true);
    try {
      const currentMaxes = lastMaxLift || {
        benchPress: 0,
        deadlift: 0,
        squat: 0,
        clean: 0
      };

      const newMaxLift = {
        userId: user.uid,
        benchPress: selectedLift === LiftType.BenchPress ? newMaxValue : currentMaxes.benchPress,
        deadlift: selectedLift === LiftType.Deadlift ? newMaxValue : currentMaxes.deadlift,
        squat: selectedLift === LiftType.Squat ? newMaxValue : currentMaxes.squat,
        clean: selectedLift === LiftType.Clean ? newMaxValue : currentMaxes.clean,
        dateCreated: Timestamp.now()
      };

      await addDoc(collection(db, 'maxLifts'), newMaxLift);
      
      // Reset form
      setSelectedLift(null);
      setNewMaxValue(0);
      setShowingAddSheet(false);
      setShowingConfirmation(false);
      
      // Reload data
      await loadMaxLifts();
    } catch (error: any) {
      console.error('Error saving max lift:', error);
      setErrorMessage(`Failed to save max lift: ${error.message}`);
    }
    setIsSaving(false);
  };

  if (isLoading) {
    return (
      <div className="max-lifts-view">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading max lifts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-lifts-view">
      <div className="max-lifts-header">
        <h1>Max Lifts</h1>
        <button 
          className="add-button"
          onClick={() => setShowingAddSheet(true)}
        >
          +
        </button>
      </div>

      {maxLifts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-content">
            <div className="empty-icon">💪</div>
            <h2>No Max Lifts Yet</h2>
            <p>Start tracking your progress by adding your first max lift!</p>
            <button 
              className="get-started-button"
              onClick={() => setShowingAddSheet(true)}
            >
              Get Started
            </button>
          </div>
        </div>
      ) : (
        <div className="max-lifts-content">
          {lastMaxLift && (
            <div className="current-maxes">
              <h2>Current Maxes</h2>
              <div className="current-maxes-grid">
                {Object.values(LiftType).map((liftType) => (
                  <div 
                    key={liftType} 
                    className="max-lift-card"
                    style={{ borderTopColor: getColorForLift(liftType) }}
                  >
                    <div className="lift-name">{liftType}</div>
                    <div className="lift-value">
                      {valueForLift(lastMaxLift, liftType)} lbs
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {sortedMaxLifts.length > 1 && (
            <div className="progress-chart">
              <h2>Progress Over Time</h2>
              <div className="chart-container">
                {Object.values(LiftType).map((liftType) => (
                  <div key={liftType} className="lift-progress">
                    <div 
                      className="lift-indicator"
                      style={{ backgroundColor: getColorForLift(liftType) }}
                    ></div>
                    <span className="lift-name-small">{liftType}</span>
                    <div className="progress-line">
                      {sortedMaxLifts.map((lift, index) => (
                        <div 
                          key={index}
                          className="progress-point"
                          style={{ 
                            backgroundColor: getColorForLift(liftType),
                            height: `${(valueForLift(lift, liftType) / Math.max(...sortedMaxLifts.map(l => valueForLift(l, liftType)))) * 60}px`
                          }}
                          title={`${valueForLift(lift, liftType)} lbs - ${lift.dateCreated.toLocaleDateString()}`}
                        ></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add Max Lift Modal */}
      {showingAddSheet && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add New Max</h2>
              <button 
                className="close-button"
                onClick={() => setShowingAddSheet(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>Select Lift</label>
                <select
                  value={selectedLift || ''}
                  onChange={(e) => setSelectedLift(e.target.value as LiftType)}
                  className="text-field"
                >
                  <option value="">Choose a lift</option>
                  {Object.values(LiftType).map((liftType) => (
                    <option key={liftType} value={liftType}>{liftType}</option>
                  ))}
                </select>
              </div>

              {selectedLift && lastMaxLift && (
                <div className="current-max-info">
                  <p>Current {selectedLift}: {valueForLift(lastMaxLift, selectedLift)} lbs</p>
                </div>
              )}

              <div className="form-group">
                <label>New Max Weight (lbs)</label>
                <input
                  type="number"
                  value={newMaxValue || ''}
                  onChange={(e) => setNewMaxValue(parseFloat(e.target.value) || 0)}
                  className="text-field"
                  min="0"
                  step="5"
                  placeholder="Enter weight"
                />
              </div>

              <div className="modal-actions">
                <button 
                  className="cancel-button"
                  onClick={() => setShowingAddSheet(false)}
                >
                  Cancel
                </button>
                <button 
                  className="confirm-button"
                  onClick={() => setShowingConfirmation(true)}
                  disabled={!selectedLift || newMaxValue <= 0}
                >
                  Add Max
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showingConfirmation && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Confirm New Max</h2>
            </div>
            
            <div className="modal-body">
              <p>Are you sure you want to update your {selectedLift} max to {newMaxValue} lbs?</p>
              
              <div className="modal-actions">
                <button 
                  className="cancel-button"
                  onClick={() => setShowingConfirmation(false)}
                >
                  Cancel
                </button>
                <button 
                  className="save-button"
                  onClick={saveMaxLift}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="error-message">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default MaxLiftsView;
